import React, { useMemo, useState } from "react";
import {
  Box, Typography, Paper, Button, TextField, Grid, Alert, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Chip, Snackbar
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import * as XLSX from "sheetjs-style";
import * as XLSXRead from "xlsx";
import { generaLinkOutlook, righeToEventi, COLONNE_INPUT, INFO_URL } from "../../utils/outlookLink";

const EVENTO_VUOTO = {
  subject: "", location: "Microsoft Teams", start: "", end: "",
  teamsUrl: "", bodyHtml: "",
};

function leggiFoglio(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSXRead.read(new Uint8Array(e.target.result), { type: "array" });
        // Preferisce il foglio "Eventi" (come lo script originale), altrimenti il primo
        const nome = wb.SheetNames.includes("Eventi") ? "Eventi" : wb.SheetNames[0];
        resolve(XLSXRead.utils.sheet_to_json(wb.Sheets[nome], { header: 1, raw: true, defval: "" }));
      } catch (err) {
        reject(new Error("Impossibile leggere il file. Verifica che sia un Excel o CSV valido."));
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

async function copia(testo) {
  await navigator.clipboard.writeText(testo);
}

function EventoSingolo({ onCopiato }) {
  const [evento, setEvento] = useState(EVENTO_VUOTO);
  const [tentato, setTentato] = useState(false);

  const set = (campo) => (e) => setEvento((prev) => ({ ...prev, [campo]: e.target.value }));
  const risultato = useMemo(() => generaLinkOutlook(evento), [evento]);
  const mancante = (campo) => tentato && !String(evento[campo]).trim();

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField label="Oggetto" fullWidth required size="small"
            value={evento.subject} onChange={set("subject")} error={mancante("subject")} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Luogo" fullWidth required size="small"
            value={evento.location} onChange={set("location")} error={mancante("location")} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="Inizio" type="datetime-local" fullWidth required size="small"
            InputLabelProps={{ shrink: true }}
            value={evento.start} onChange={set("start")} error={mancante("start")} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label="Fine" type="datetime-local" fullWidth required size="small"
            InputLabelProps={{ shrink: true }}
            value={evento.end} onChange={set("end")} error={mancante("end")} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Orari intesi come ora italiana (Europe/Rome); nel link vengono convertiti in UTC.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Link partecipazione Teams" fullWidth size="small"
            value={evento.teamsUrl} onChange={set("teamsUrl")} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            "Altre informazioni" rimanda sempre alla{" "}
            <a href={INFO_URL} target="_blank" rel="noopener noreferrer">guida Microsoft per partecipare a una riunione Teams</a>.
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField label="Corpo HTML personalizzato (opzionale)" fullWidth multiline minRows={3} size="small"
            value={evento.bodyHtml} onChange={set("bodyHtml")}
            helperText="Se vuoto, viene usato il template standard &quot;Riunione di Microsoft Teams&quot; con il link Teams e il link Altre informazioni." />
        </Grid>
      </Grid>

      <Box mt={3}>
        {risultato.link ? (
          <>
            <TextField label="Link Outlook" fullWidth size="small" value={risultato.link}
              InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
            <Box display="flex" gap={1} mt={1.5}>
              <Button variant="contained" startIcon={<ContentCopyIcon />}
                onClick={() => copia(risultato.link).then(onCopiato)}>
                Copia link
              </Button>
              <Button variant="outlined" startIcon={<OpenInNewIcon />}
                href={risultato.link} target="_blank" rel="noopener noreferrer">
                Prova in Outlook
              </Button>
            </Box>
          </>
        ) : tentato ? (
          <Alert severity="warning">{risultato.errore}</Alert>
        ) : (
          <Button variant="contained" onClick={() => setTentato(true)}>Genera link</Button>
        )}
      </Box>
    </Box>
  );
}

function DaFile({ onCopiato }) {
  const [file, setFile] = useState(null);
  const [righe, setRighe] = useState(null);
  const [intestazione, setIntestazione] = useState([]);
  const [risultati, setRisultati] = useState(null);
  const [errore, setErrore] = useState(null);

  async function caricaFile(f) {
    setFile(f);
    setErrore(null);
    setRisultati(null);
    if (!f) return;
    try {
      const dati = await leggiFoglio(f);
      const eventi = righeToEventi(dati);
      if (!eventi.length) throw new Error("Il file non contiene righe dopo l'intestazione.");
      setRighe(dati);
      setIntestazione(dati[0] || []);
      setRisultati(eventi.map((ev) => ({ ...ev, ...generaLinkOutlook(ev) })));
    } catch (err) {
      setRighe(null);
      setErrore(err.message);
    }
  }

  function esportaExcel() {
    // Riproduce il foglio caricato aggiungendo la colonna H con il link (o l'errore), come lo script
    const perRiga = new Map(risultati.map((r) => [r.riga, r.link || `[ERRORE: ${r.errore}]`]));
    const colH = COLONNE_INPUT.length;
    const aoa = righe.map((r, idx) => {
      const riga = [...r];
      while (riga.length < colH) riga.push("");
      riga[colH] = idx === 0 ? "Link Outlook" : (perRiga.get(idx + 1) || "");
      return riga;
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 40 }, { wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 30 }, { wch: 30 }, { wch: 30 }, { wch: 80 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Eventi");
    const base = file?.name?.replace(/\.[^.]+$/, "") || "Eventi";
    XLSX.writeFile(wb, `${base}_link_outlook.xlsx`);
  }

  const nOk = risultati?.filter((r) => r.link).length || 0;
  const nErr = risultati ? risultati.length - nOk : 0;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Carica un Excel (foglio "Eventi" o il primo foglio) con una riga di intestazione e le colonne nell'ordine:{" "}
        {COLONNE_INPUT.map((c, i) => `${String.fromCharCode(65 + i)} ${c.label}`).join(" · ")}.
        Le date possono essere celle data/ora oppure testo "dd/MM/yyyy HH:mm" (ora italiana).
        La colonna F viene ignorata: "Altre informazioni" rimanda sempre alla guida Microsoft per partecipare a Teams.
        La colonna G è opzionale: se vuota si usa il template standard.
      </Typography>

      <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}
        color={risultati ? "success" : "primary"}>
        {file ? file.name : "Seleziona file"}
        <input type="file" accept=".xlsx,.xls,.csv" hidden
          onChange={(e) => { caricaFile(e.target.files[0] || null); e.target.value = ""; }} />
      </Button>

      {errore && <Alert severity="error" sx={{ mt: 2 }}>{errore}</Alert>}

      {risultati && (
        <Box mt={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" gap={1}>
              <Chip color="success" label={`${nOk} link generati`} />
              {nErr > 0 && <Chip color="error" label={`${nErr} righe con errori`} />}
            </Box>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={esportaExcel}>
              Esporta Excel con link
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f0f4ff" }}>
                  <TableCell><strong>Riga</strong></TableCell>
                  <TableCell><strong>{intestazione[0] || "Subject"}</strong></TableCell>
                  <TableCell><strong>Esito</strong></TableCell>
                  <TableCell align="right"><strong>Azioni</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {risultati.map((r) => (
                  <TableRow key={r.riga} hover>
                    <TableCell>{r.riga}</TableCell>
                    <TableCell>{String(r.subject || "—")}</TableCell>
                    <TableCell>
                      {r.link
                        ? <Typography variant="body2" color="success.main">Link pronto</Typography>
                        : <Typography variant="body2" color="error">{r.errore}</Typography>}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {r.link && (
                        <>
                          <Tooltip title="Copia link">
                            <IconButton size="small" onClick={() => copia(r.link).then(onCopiato)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Apri in Outlook">
                            <IconButton size="small" href={r.link} target="_blank" rel="noopener noreferrer">
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

export default function LinkOutlook() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [copiato, setCopiato] = useState(false);
  const onCopiato = () => setCopiato(true);

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/utility")}
          variant="outlined" size="small">
          Utility
        </Button>
        <Box>
          <Typography variant="h5" fontWeight="bold">Link calendario Outlook</Typography>
          <Typography variant="body2" color="text.secondary">
            Genera i link che aprono Outlook Web App con l'evento già compilato, pronto da salvare nel calendario
          </Typography>
        </Box>
      </Box>

      <Paper elevation={1} sx={{ p: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
          <Tab label="Singolo evento" />
          <Tab label="Da file Excel" />
        </Tabs>
        {tab === 0 ? <EventoSingolo onCopiato={onCopiato} /> : <DaFile onCopiato={onCopiato} />}
      </Paper>

      <Snackbar open={copiato} autoHideDuration={2000} onClose={() => setCopiato(false)}
        message="Link copiato negli appunti" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
    </Box>
  );
}
